import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
    const { clear } = useCart();
    const { refresh } = useAuth();
    const attempts = useRef(0);
    const cleared = useRef(false);

    useEffect(() => {
        if (!sessionId) { setStatus("error"); return; }
        let timer;
        const poll = async () => {
            attempts.current += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    if (!cleared.current) { cleared.current = true; clear(); refresh?.().catch(() => {}); }
                    return;
                }
                if (attempts.current >= 8) { setStatus("timeout"); return; }
                timer = setTimeout(poll, 2000);
            } catch {
                if (attempts.current >= 8) { setStatus("error"); return; }
                timer = setTimeout(poll, 2000);
            }
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId]);

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-5">
            <div className="text-center max-w-md" data-testid="payment-success">
                {status === "checking" && <><Loader2 className="w-14 h-14 text-gold animate-spin mx-auto" /><h1 className="mt-6 font-serif text-4xl text-cream">Confirming your payment…</h1><p className="mt-3 text-ash">One moment while we set the light.</p></>}
                {status === "paid" && <><CheckCircle2 className="w-16 h-16 text-gold mx-auto" /><h1 className="mt-6 font-serif text-4xl text-cream">Thank you.</h1><p className="mt-3 text-ash">Your payment is confirmed. A confirmation is on its way to your email, and the house begins the work.</p><Link to="/account" className="inline-block mt-7 bg-gold text-ink rounded-full px-8 py-3 font-medium hover:bg-goldbright transition-colors">View my account</Link></>}
                {status === "timeout" && <><Loader2 className="w-14 h-14 text-gold mx-auto" /><h1 className="mt-6 font-serif text-4xl text-cream">Still processing</h1><p className="mt-3 text-ash">Your payment is taking a little longer. Check your account shortly.</p><Link to="/account" className="inline-block mt-7 text-gold border-b border-gold/40 pb-1">Go to account</Link></>}
                {status === "error" && <><XCircle className="w-14 h-14 text-clay mx-auto" /><h1 className="mt-6 font-serif text-4xl text-cream">Hmm, something's off</h1><p className="mt-3 text-ash">We couldn't confirm this session. If you were charged, contact Sherrie.</p><Link to="/" className="inline-block mt-7 text-gold">Return home</Link></>}
            </div>
        </div>
    );
}
